import React from 'react';
import Chart from 'react-apexcharts';
import { formatters } from 'services';

/** One scatter point. `x`/`y` are already scaled to % (2dp) by the caller, so
 *  this component is unit-agnostic. `label` is the player name in the trainer
 *  (cohort) view and omitted in the anonymised trainee (self) view — where the
 *  tooltip falls back to "You"/"Peer". */
export type ScatterPoint = {
  x: number;
  y: number;
  isSelf: boolean;
  label?: string;
};

type Props = {
  points: ScatterPoint[];
  height?: number | string;
};

/** Risk/return scatter (last return vs volatility), the self point highlighted.
 *  Mode-agnostic: the trainer view passes every player's point with a name; the
 *  trainee view passes identity-stripped points (tooltip reads "You"/"Peer"). */
const RiskReturnScatter: React.FC<Props> = ({ points, height = '100%' }) => {
  const others = points.filter(p => !p.isSelf);
  const selfPoints = points.filter(p => p.isSelf);

  const series = [
    {
      name: 'Cohort',
      data: others.map(p => ({ x: p.x, y: p.y, name: p.label ?? 'Peer' })),
    },
    {
      name: 'Selected',
      data: selfPoints.map(p => ({ x: p.x, y: p.y, name: p.label ?? 'You' })),
    },
  ];

  const options: any = {
    chart: {
      type: 'scatter',
      height,
      parentHeightOffset: 0,
      toolbar: { show: false },
      animations: { enabled: false },
      zoom: { enabled: false },
    },
    colors: ['#9fb3cd', '#0f5fd6'],
    markers: { size: [6, 11], strokeWidth: 0 },
    grid: { padding: { top: 0, right: 8, bottom: 0, left: 8 } },
    legend: { show: false },
    dataLabels: { enabled: false },
    xaxis: {
      title: {
        text: 'Volatility',
        style: { fontSize: '11px', fontWeight: 500 },
      },
      tickAmount: 5,
      labels: {
        formatter: (v: number) => `${Number(v).toFixed(0)}%`,
        style: { fontSize: '11px' },
      },
    },
    yaxis: {
      title: { text: 'Return', style: { fontSize: '11px', fontWeight: 500 } },
      labels: {
        formatter: (v: number) => `${Number(v).toFixed(0)}%`,
        style: { fontSize: '11px' },
      },
    },
    tooltip: {
      custom: ({ seriesIndex, dataPointIndex, w }: any) => {
        const pt = w.config.series[seriesIndex].data[dataPointIndex];
        if (!pt) return '';
        return `<div style="padding:6px 8px;font-size:12px">
          <strong>${pt.name}</strong><br/>
          Return ${formatters.commas2Digits(pt.y)}%<br/>
          Vol ${formatters.commas2Digits(pt.x)}%
        </div>`;
      },
    },
  };

  return (
    <div style={{ height }}>
      <Chart options={options} series={series} type="scatter" height={height} />
    </div>
  );
};

export default RiskReturnScatter;
